/*
    For historical reasons, this file is still called naics.ts, and several of our models
    (e.g. asset_corporate) have a naics_code field in the database.

    However, it is actually BEA codes that are used by CEE.

    As of February 2023, users had to select one of the COMPANY_ESTIMATE_INDUSTRY_CODES
    for their industry code. But we expanded the set of available codes by performing
    mappings between:

      1. The complete NAICS code set and BEA
      2. GICS codes and BEA
      3. NACE codes and BEA
      4. SICS codes and BEA
      5. ANZSIC codes and BEA
      6. UKSIC codes and BEA (09/2024)

    As of this change, users are presented with a single list of all NAICS and GICS codes
    all together, since the field is an autocomplete, and it would add unwanted to complexity
    to ask users to choose which system to use.

    We now store a raw NAICS code in the case of NAICS or GICS_[code] in the case of GICS.
    This leaves open the possibility of adding other mappings in the future.

    When we run CEE, we map the stored value to an EEIO supported member of the
    COMPANY_ESTIMATE_INDUSTRY_CODES list.
 */

import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import { ANZSIC_CODES } from './anzsic';
import { GICS_CODES, HISTORICAL_GICS_CODES } from './gics';
import { GICS_SECTORS_CODES } from './gics_sectors';
import { NACE_CODES } from './nace';
import { NAICS_CODES } from './naics';
import { NAICS_SECTOR_CODES } from './naics_sectors';
import {
  COMPANY_ESTIMATE_INDUSTRY_CODES,
  NAICS_SECTORS,
  NAICS_SUBSECTORS,
} from './otherCodes';
import { SICS_CODES } from './sics';
import { SP_NAICS_CODES } from './sp_naics';
import { UKSIC_CODES } from './uksic';

const INDUSTRY_CODE_SOURCES = [
  'NAICS',
  'NAICS_SECTOR',
  'GICS',
  'GICS_SECTOR',
  'NACE',
  'SICS',
  'ANZSIC',
  'UKSIC',
] as const;
export type IndustryCodeSource = (typeof INDUSTRY_CODE_SOURCES)[number];

export const INDUSTRY_CODE_MAPPINGS: Record<
  IndustryCodeSource,
  Record<
    string,
    {
      label: string;
      BEA_CODES: WeightedBeaCodes;
    }
  >
> = {
  ['NAICS']: NAICS_CODES,
  ['NAICS_SECTOR']: NAICS_SECTOR_CODES,
  ['GICS']: GICS_CODES,
  ['GICS_SECTOR']: GICS_SECTORS_CODES,
  ['NACE']: NACE_CODES,
  ['SICS']: SICS_CODES,
  ['ANZSIC']: ANZSIC_CODES,
  ['UKSIC']: UKSIC_CODES,
};

// These are from https://www.bea.gov/naics_2022
const ADDITIONAL_BEA_CODES_TO_SECTORS: { [key: string]: string | undefined } = {
  // Although naics_2022 specifies 491000 to be under "Government", in our sector mapping
  // logic, 491000 has a valid sector code of 49, which maps to "Transportation and Warehousing"
  // (See workspaces/shared-universal/industryCodes/otherCodes.ts)
  // So realistically, we will never look up 491000 from this mapping. But we include it here
  // for completeness.
  '491000': 'Government',
  S00500: 'Government',
  S00600: 'Government',
  S00101: 'Government',
  S00102: 'Government',
  GSLGE: 'Government',
  GSLGH: 'Government',
  GSLGO: 'Government',
  S00201: 'Government',
  S00202: 'Government',
  S00203: 'Government',
  S00401: 'Scrap, Used and Secondhand Goods',
  S00402: 'Scrap, Used and Secondhand Goods',
  // The following two codes are originally "S003" and "S009" according to the BEA code table
  // but we are using the 6-digit codes here, to be consistent with the rest of the BEA codes
  // in workspaces/shared-universal/beaCode.ts
  S00300: 'Noncomparable Imports and Rest-of-the-world Adjustment',
  S00900: 'Noncomparable Imports and Rest-of-the-world Adjustment',
};

export type WeightedBeaCodes = Partial<{
  [key in keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES]: number;
}>;

export function getIndustrySectorCode(industryCode: string): string {
  const beaCode = getBeaCodeFromIndustryCode(industryCode);
  return beaCode.substring(0, 2);
}

function subsectorFromNaicsCode(naicsCode: string): string | null {
  const naicsSubSectorCode = naicsCode.substring(0, 3);
  return NAICS_SUBSECTORS[naicsSubSectorCode] ?? null;
}

export function sectorFromNaicsCode(naicsCode: string): string | null {
  const sectorCode = naicsCode.substring(0, 2);
  if (sectorCode in NAICS_SECTORS) {
    return NAICS_SECTORS[sectorCode] ?? null;
  }

  if (naicsCode in ADDITIONAL_BEA_CODES_TO_SECTORS) {
    return ADDITIONAL_BEA_CODES_TO_SECTORS[naicsCode] ?? null;
  }

  return null;
}

export function getNaicsSectorForIndustryCode(industryCode: string): string {
  const beaCode = getBeaCodeFromIndustryCode(industryCode);
  return sectorFromNaicsCode(beaCode) ?? 'Unknown';
}

// Cache for `findIndustryCodeFromCodeOrLabel`
const industryCodeLookupMap = new Map<string, string>();

/**
 * All industry codes across all taxonomies.
 */
export const INDUSTRY_CODE_LIST_INCLUDING_DEPRECATED = sortBy(
  uniq([
    ...Object.keys(COMPANY_ESTIMATE_INDUSTRY_CODES),
    ...Object.values(INDUSTRY_CODE_MAPPINGS).flatMap((mapping) =>
      Object.keys(mapping)
    ),
  ]),
  (code) => getIndustryFromIndustryCode(code)
);

export const INDUSTRY_CODE_LIST =
  INDUSTRY_CODE_LIST_INCLUDING_DEPRECATED.filter(
    (value) => !HISTORICAL_GICS_CODES[value]
  );

export const INDUSTRY_CODE_SET_INCLUDING_DEPRECATED = new Set(
  INDUSTRY_CODE_LIST_INCLUDING_DEPRECATED
);

export const INDUSTRY_CODE_LIST_OPTIONS = INDUSTRY_CODE_LIST.map((value) => ({
  value,
  display: getIndustryLabelFromIndustryCode(value, { includeCode: false }),
}));

export function isValidIndustryCode(code?: string | null): boolean {
  return (
    !!code && INDUSTRY_CODE_SET_INCLUDING_DEPRECATED.has(code.toUpperCase())
  );
}

export function findIndustryCodeFromCodeOrLabel(
  input: string
): string | undefined {
  if (isValidIndustryCode(input)) return input.toUpperCase();

  // Lazily initialize cache
  if (industryCodeLookupMap.size === 0) {
    function addIfNotExists(key: string, code: string) {
      if (!industryCodeLookupMap.has(key)) {
        industryCodeLookupMap.set(key, code);
      }
    }
    for (const [code, label] of Object.entries(
      COMPANY_ESTIMATE_INDUSTRY_CODES
    )) {
      addIfNotExists(label, code);
      addIfNotExists(formatLabelAndCode(label, code), code);
    }
    for (const mapping of Object.values(INDUSTRY_CODE_MAPPINGS)) {
      for (const [code, { label }] of Object.entries(mapping)) {
        addIfNotExists(label, code);
        addIfNotExists(formatLabelAndCode(label, code), code);
      }
    }
  }

  return industryCodeLookupMap.get(input);
}

function formatLabelAndCode(label: string, code: string): string {
  const codeUpperCase = code.toUpperCase();
  return `${label} (${codeUpperCase})`;
}

/**
 * Returns a CEE compatible code
 * When one code maps to multiple CEE codes, it returns the code with the highest weight
 * If the code is not found, it throws an error
 */
export function getBeaCodeFromIndustryCode(
  code: string
): keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES {
  const codeUpperCase = code.toUpperCase();
  if (
    COMPANY_ESTIMATE_INDUSTRY_CODES[
      codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ]
  ) {
    return codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES;
  }
  for (const mapping of Object.values(INDUSTRY_CODE_MAPPINGS)) {
    if (mapping[codeUpperCase]) {
      const beaCodes = mapping[codeUpperCase].BEA_CODES;
      return getMaxBeaCode(beaCodes);
    }
  }
  throw new Error(`Invalid industry code: ${codeUpperCase}`);
}

function getMaxBeaCode(
  beaCodes: WeightedBeaCodes
): keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES {
  const [highestWeightedBeaCode] = Object.entries(beaCodes).reduce((a, b) =>
    a[1] > b[1] ? a : b
  );
  return highestWeightedBeaCode as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES;
}

export function getMultiBeaCodeFromIndustryCode(
  code: string
): WeightedBeaCodes {
  const codeUpperCase = code.toUpperCase();
  if (
    COMPANY_ESTIMATE_INDUSTRY_CODES[
      codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ]
  ) {
    return {
      [codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES]: 1.0,
    };
  }
  for (const mapping of Object.values(INDUSTRY_CODE_MAPPINGS)) {
    if (mapping[codeUpperCase]) {
      return mapping[codeUpperCase].BEA_CODES;
    }
  }
  throw new Error(`Invalid industry code: ${codeUpperCase}`);
}

/**
 * Weights for disaggregating the Watershed-invented aggregate logistics code
 * (480000) into its constituent transport sector BEA codes. Weights are based
 * on the total aggregate economic output of each sector in the economy
 * (`x` vector in USEEIO).
 *
 * Keep in sync with py/watershed/domain/cee/constants/physical.py LOGISTICS_DISAGGREGATION.
 */
const LOGISTICS_DISAGGREGATION: WeightedBeaCodes = {
  '481000': 0.3162,
  '482000': 0.127,
  '483000': 0.0753,
  '484000': 0.48,
};

const AGGREGATE_LOGISTICS_CODE = '480000';

/**
 * Replaces the aggregate logistics code (480000) with its disaggregated
 * transport sub-codes, distributing the original weight proportionally.
 *
 * CEE handles this disaggregation internally in Python, but VEE needs it
 * applied on the TypeScript side before passing codes to the estimation model.
 */
export function disaggregateLogisticsCode(
  weightedBeaCodes: WeightedBeaCodes
): WeightedBeaCodes {
  const logisticsWeight =
    weightedBeaCodes[
      AGGREGATE_LOGISTICS_CODE as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ];
  if (logisticsWeight == null) {
    return weightedBeaCodes;
  }

  const result: Record<string, number> = {
    ...weightedBeaCodes,
  };
  delete result[AGGREGATE_LOGISTICS_CODE];
  for (const [subCode, subWeight] of Object.entries(LOGISTICS_DISAGGREGATION)) {
    result[subCode] = (result[subCode] ?? 0) + logisticsWeight * subWeight;
  }

  return result as WeightedBeaCodes;
}

/**
 * Codes that are not intended for use as standalone industries in EEIO models.
 * They must be remapped to valid codes to prevent crashes in the estimation model.
 *
 * Keep in sync with py/watershed/domain/cee/validation.py REMAPPING_FOR_CEDA (lines 77–83).
 * not identical to REMAPPING_FOR_CEDA because 562000 isn't a bea code.
 */
const ADJUSTMENT_CODE_REMAPPING: Record<string, string> = {
  S00300: 'S00600',
  S00401: '562111',
  S00402: '562111',
  S00900: 'S00600',
};

/**
 * Remaps EEIO adjustment codes (S00300, S00401, S00402, S00900) to valid
 * industry codes. These codes are not meant for company estimations and would
 * crash the estimation model if left unmapped.
 *
 * CEE handles this remapping in Python (validation.py), but VEE needs it
 * applied on the TypeScript side before passing codes to the estimation model.
 */
export function remapAdjustmentCodes(
  weightedBeaCodes: WeightedBeaCodes
): WeightedBeaCodes {
  const result: Record<string, number> = { ...weightedBeaCodes };
  for (const [fromCode, toCode] of Object.entries(ADJUSTMENT_CODE_REMAPPING)) {
    const weight = result[fromCode];
    if (weight != null) {
      delete result[fromCode];
      result[toCode] = (result[toCode] ?? 0) + weight;
    }
  }
  return result as WeightedBeaCodes;
}

export function getTaxonomyFromIndustryCode(
  code: string,
  sources?: ReadonlyArray<IndustryCodeSource>
): string {
  const codeUpperCase = code.toUpperCase();
  if (
    COMPANY_ESTIMATE_INDUSTRY_CODES[
      codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ]
  )
    return 'BEA';
  for (const source of sources ?? INDUSTRY_CODE_SOURCES) {
    if (INDUSTRY_CODE_MAPPINGS[source][codeUpperCase]) {
      return source;
    }
  }
  return '';
}

// Just the industry string itself
export function getIndustryFromIndustryCode(
  code: string,
  sources?: ReadonlyArray<IndustryCodeSource>
): string {
  const codeUpperCase = code.toUpperCase();
  if (
    COMPANY_ESTIMATE_INDUSTRY_CODES[
      codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ]
  )
    return COMPANY_ESTIMATE_INDUSTRY_CODES[
      codeUpperCase as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ];
  for (const source of sources ?? INDUSTRY_CODE_SOURCES) {
    if (INDUSTRY_CODE_MAPPINGS[source][codeUpperCase]) {
      return INDUSTRY_CODE_MAPPINGS[source][codeUpperCase].label;
    }
  }
  return '';
}

type IndustryCodeLabelOptions = {
  includeCode?: boolean;
  includeTaxonomy?: boolean;
  sources?: ReadonlyArray<IndustryCodeSource>;
};
const DEFAULT_INDUSTRY_CODE_LABEL_OPTIONS: IndustryCodeLabelOptions = {
  includeCode: true,
  includeTaxonomy: false,
};

export function getIndustryLabelFromIndustryCode(
  code: string,
  options: IndustryCodeLabelOptions = DEFAULT_INDUSTRY_CODE_LABEL_OPTIONS
): string {
  const codeUpperCase = code.toUpperCase().replace('NAICS_', '');
  const label = getIndustryFromIndustryCode(codeUpperCase, options.sources);
  if (label === '') return code;
  const prefix = options.includeTaxonomy
    ? `[${getTaxonomyFromIndustryCode(codeUpperCase, options.sources)}] `
    : '';
  const suffix = options.includeCode
    ? formatLabelAndCode(label, codeUpperCase)
    : label;
  return `${prefix}${suffix}`;
}

export function safelyGetIndustryLabelFromIndustryCode(
  code: string | null,
  options: IndustryCodeLabelOptions = DEFAULT_INDUSTRY_CODE_LABEL_OPTIONS
): string {
  return code ? getIndustryLabelFromIndustryCode(code, options) : '';
}

export function getMostSpecificNaicsIndustry(
  naicsCode: string | null
): string | null {
  if (!naicsCode) {
    return null;
  }

  const formattedNaicsCode = naicsCode.toUpperCase();

  const naicsIndustry =
    COMPANY_ESTIMATE_INDUSTRY_CODES[
      // reasonable cast here because we're testing for the key in the object
      formattedNaicsCode as keyof typeof COMPANY_ESTIMATE_INDUSTRY_CODES
    ];
  if (naicsIndustry) {
    return naicsIndustry;
  }

  const naicsSubSector = subsectorFromNaicsCode(formattedNaicsCode);
  if (naicsSubSector) {
    return naicsSubSector;
  }

  const naicsSector = sectorFromNaicsCode(formattedNaicsCode);
  return naicsSector ?? null;
}

/**
 * Since we ingest company NAICS codes from S&P, we want to make sure
 * that we have a mapping for all of them. This function first checks
 * the existing mappings and then the remaining S&P mappings for NAICS.
 * This is used specifically for _companies_ and shouldn't be used elsewhere.
 */
export function getIndustryLabelFromNaicsCode(
  code: string,
  optionsWithoutSource: Omit<IndustryCodeLabelOptions, 'sources'> & {
    prioritizeSAndPNaics?: boolean;
  } = DEFAULT_INDUSTRY_CODE_LABEL_OPTIONS
): string {
  const prioritizeSAndPNaics =
    optionsWithoutSource.prioritizeSAndPNaics ?? true;
  const prioritizedSources: ReadonlyArray<IndustryCodeSource> =
    prioritizeSAndPNaics ? ['NAICS', 'NAICS_SECTOR'] : INDUSTRY_CODE_SOURCES;

  if (getIndustryFromIndustryCode(code, prioritizedSources) !== '') {
    return getIndustryLabelFromIndustryCode(code, {
      ...optionsWithoutSource,
      sources: prioritizedSources,
    });
  } else if (SP_NAICS_CODES[code]) {
    const label = SP_NAICS_CODES[code];
    const prefix = optionsWithoutSource.includeTaxonomy ? `[NAICS] ` : '';
    const suffix = optionsWithoutSource.includeCode
      ? formatLabelAndCode(label, code)
      : label;
    return `${prefix}${suffix}`;
  } else if (prioritizeSAndPNaics && getIndustryFromIndustryCode(code) !== '') {
    // If we prioritized S&P naics, try all other sources
    return getIndustryLabelFromIndustryCode(code, optionsWithoutSource);
  }
  return '';
}

export function getTaxonomyFromNaicsCode(
  code: string,
  prioritizeSAndPNaics?: boolean
): string {
  const sources: ReadonlyArray<IndustryCodeSource> =
    (prioritizeSAndPNaics ?? true)
      ? ['NAICS', 'NAICS_SECTOR']
      : INDUSTRY_CODE_SOURCES;

  const taxonomy = getTaxonomyFromIndustryCode(code, sources);
  if (taxonomy !== '') {
    return taxonomy;
  } else if (SP_NAICS_CODES[code]) {
    return 'NAICS';
  } else if (prioritizeSAndPNaics) {
    const taxonomyForAllSources = getTaxonomyFromIndustryCode(code);
    if (taxonomyForAllSources !== '') {
      return taxonomyForAllSources;
    }
  }
  return '';
}
